import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PasswordStrengthIndicator } from '@/features/auth/components/password-strength-indicator';
import { UpdatePasswordInProgress } from '@/features/auth/components/update-password-in-progress';
import { UpdatePasswordSuccessMessage } from '@/features/auth/components/update-password-success-message';
import { useUpdatePassword } from '@/features/auth/hooks/use-update-password';
import {
  UpdatePasswordInput,
  updatePasswordInputSchema,
  ResetPasswordNewInput,
  resetPasswordNewSchema,
} from '@/features/auth/validation/update-password-input.schema';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import { PasswordInput } from '@/shared/components/ui/password-input';
import { useToast } from '@/shared/hooks/use-toast';

interface UpdatePasswordFormProps {
  isFromSettings?: boolean;
}

export const UpdatePasswordForm = ({
  isFromSettings = false,
}: UpdatePasswordFormProps) => {
  const { t } = useTranslation('auth', { keyPrefix: 'UpdatePasswordForm' });

  const { t: tFormFields } = useTranslation('common', {
    keyPrefix: 'form_field',
  });

  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const form = useForm<UpdatePasswordInput | ResetPasswordNewInput>({
    resolver: zodResolver(
      isFromSettings ? updatePasswordInputSchema : resetPasswordNewSchema,
    ),
  });

  const { mutate, isPending, isSuccess } = useUpdatePassword();

  const { toast } = useToast();
  const onSubmit = async (
    data: UpdatePasswordInput | ResetPasswordNewInput,
  ) => {
    mutate(data, {
      onError: (error) => {
        if (error.message === 'current_password_incorrect') {
          toast({
            title: t('general_error_title'),
            description: t('current_password_error'),
            variant: 'destructive',
          });
        } else {
          toast({
            title: t('general_error_title'),
            description: t('general_error_description'),
            variant: 'destructive',
          });
        }
      },
    });
  };

  if (isPending) {
    return <UpdatePasswordInProgress />;
  }

  if (isSuccess) {
    return <UpdatePasswordSuccessMessage />;
  }
  return (
    <Card variant="form">
      <CardHeader>
        <CardTitle className="text-center text-4xl font-medium leading-tight tracking-[-0.04em]">
          {t('title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col items-center gap-6"
          >
            {isFromSettings && (
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <PasswordInput
                        label={t('current_password_label')}
                        placeholder={t('current_password_placeholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <PasswordInput
                      label={t('new_password_label')}
                      placeholder={t('new_password_placeholder')}
                      {...field}
                      error={!isPasswordValid}
                    />
                  </FormControl>
                  <PasswordStrengthIndicator
                    password={field.value ?? ''}
                    onRequirementsMet={setIsPasswordValid}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="newPasswordConfirmation"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <PasswordInput
                      label={t('confirm_new_password_label')}
                      placeholder={t('confirm_new_password_placeholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage
                    t={(key) =>
                      tFormFields(`confirm_password.errors.${key}` as any)
                    }
                  />
                </FormItem>
              )}
            />
            <div className="mt-12"></div>
            <Button
              type="submit"
              variant={'greenGradient'}
              btnType="primary"
              disabled={isPending}
            >
              {t('submit_button_label')}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
